// src/components/LandingPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import './LandingPage.css';

const detectDeviceOS = () => {
  const ua = (navigator.userAgent || '').toLowerCase();
  if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) return 'iOS';
  if (ua.includes('android')) return 'Android';
  return 'Android';
};

const ANDROID_VERSIONS = [
  { val: 'Android 17', label: 'Android 17' },
  { val: 'Android 16', label: 'Android 16' },
  { val: 'Android 15', label: 'Android 15' },
  { val: 'Android 14', label: 'Android 14' },
  { val: 'Android 13', label: 'Android 13' },
  { val: 'Android 12', label: 'Android 12' },
  { val: 'Android 11', label: 'Android 11' },
  { val: 'Android 10', label: 'Android 10' },
  { val: 'Android 9', label: 'Android 9 (Pie)' },
  { val: 'Other', label: 'Other / Custom Version' }
];

const IOS_VERSIONS = [
  { val: 'iOS 18', label: 'iOS 18 (Latest)' },
  { val: 'iOS 17', label: 'iOS 17' },
  { val: 'iOS 16', label: 'iOS 16' },
  { val: 'iOS 15', label: 'iOS 15' },
  { val: 'iOS 14', label: 'iOS 14' },
  { val: 'Other', label: 'Other / Custom Version' }
];

export default function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [userName, setUserName] = useState('');
  const [showReqModal, setShowReqModal] = useState(false);

  // Form State for Embedded Registration
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile_no: '',
    dob: '',
    os: detectDeviceOS(),
    os_version: detectDeviceOS() === 'iOS' ? 'iOS 17' : 'Android 14',
    custom_version: ''
  });

  const [loading, setLoading] = useState(false);
  const [regError, setRegError] = useState('');
  const [consentAccepted, setConsentAccepted] = useState(false);

  // Auto-detect referral code from URL query (e.g. ?ref=souradip)
  const queryParams = new URLSearchParams(location.search);
  const referralFromUrl = queryParams.get('ref') || '';

  useEffect(() => {
    const storedName = sessionStorage.getItem('userName');
    if (storedName) {
      setUserName(storedName);
    }
  }, []);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'os') {
      const defaultVersion = value === 'iOS' ? 'iOS 17' : (value === 'Android' ? 'Android 14' : '');
      setFormData({
        ...formData,
        os: value,
        os_version: defaultVersion,
        custom_version: ''
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const validateForm = () => {
    const { name, email, mobile_no, dob, os, os_version, custom_version } = formData;

    if (!name.trim()) {
      setRegError('Full Name is required');
      return false;
    }
    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setRegError('A valid email address is required');
      return false;
    }
    if (!mobile_no.trim() || !/^\d{10,15}$/.test(mobile_no.replace(/\D/g, ''))) {
      setRegError('Valid mobile number is required (10-15 digits)');
      return false;
    }
    if (!dob) {
      setRegError('Date of birth is required');
      return false;
    }
    if (!os) {
      setRegError('Please select Operating System (Android or iOS)');
      return false;
    }
    if (!os_version) {
      setRegError(`Please select your ${os} version`);
      return false;
    }
    if (os_version === 'Other' && !custom_version.trim()) {
      setRegError('Please enter your specific OS version');
      return false;
    }
    if (!consentAccepted) {
      setRegError('You must accept the consent agreement to proceed');
      return false;
    }
    return true;
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setRegError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const finalVersion = formData.os_version === 'Other'
        ? `${formData.os} ${formData.custom_version.trim()}`
        : formData.os_version;

      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        mobile_no: formData.mobile_no.trim(),
        dob: formData.dob,
        android_version: finalVersion,
        device_os: formData.os,
        country: 'IN',
        gender: 'Male',
        ethnicity: 'south_asian',
        referral_by: referralFromUrl
      };

      const response = await apiService.registerUser(payload);
      const userId = response.user_id;
      const registeredName = response.name;

      sessionStorage.setItem('userId', String(userId));
      sessionStorage.setItem('userName', registeredName);
      setUserName(registeredName);

      // Navigate directly into verification capture flow
      navigate(`/upload/${encodeURIComponent(registeredName)}`);
    } catch (err) {
      console.error('Registration error:', err);
      setRegError(err.response?.data?.error || 'Registration failed. Please check your details and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('userId');
    sessionStorage.removeItem('userName');
    setUserName('');
  };

  const currentVersionList = formData.os === 'iOS' ? IOS_VERSIONS : ANDROID_VERSIONS;

  return (
    <div className="landing-page-container">
      {/* 1. Header with Frinksyn Trust Badges */}
      <header className="landing-header">
        <div className="landing-brand-group">
          <div className="brand-logo-badge">
            <img src="/logo192.png" alt="Frinksyn" className="landing-logo-img" />
            <span className="brand-name-text">Frinksyn</span>
          </div>
        </div>

        <div className="landing-auth-actions">
          {/* Quick Admin Access Button on Landing Page */}
          <Link
            to={sessionStorage.getItem('adminToken') ? "/admin" : "/admin/login"}
            className="btn-landing-admin"
            title="Open Staff & Admin Portal"
          >
            <span>🛡️</span>
            <span>Admin</span>
          </Link>

          {userName ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <button
                type="button"
                className="btn-landing-signin"
                onClick={() => navigate(`/dashboard/${encodeURIComponent(userName)}`)}
              >
                Dashboard ({userName})
              </button>
              <button
                type="button"
                className="btn-landing-register"
                onClick={() => navigate(`/upload/${encodeURIComponent(userName)}`)}
              >
                Resume
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn-landing-signin"
              onClick={() => navigate('/login')}
            >
              Sign In
            </button>
          )}
        </div>
      </header>

      {/* 2. Hero Section: Left Pitch & Right Embedded Registration Form */}
      <main className="landing-hero-section">
        <div className="landing-hero-grid">
          {/* Hero Left Column: Overview & Project Pitch */}
          <div className="hero-left-col">
            <span className="landing-project-badge">
              Biometric Identity Verification
            </span>
            <h1 className="landing-hero-title">
              Secure Human Identity Verification Portal
            </h1>
            <p className="landing-hero-subtitle">
              Welcome to the verification portal. Complete your authentic portrait and head orientation captures with guided feedback.
            </p>

            <div className="hero-feature-pills">
              <div className="feature-pill">
                <span className="pill-check">✓</span>
                <span>18+ Historical Photos (No wait times)</span>
              </div>
              <div className="feature-pill">
                <span className="pill-check">✓</span>
                <span>5 Live Selfies (with 1 minutes gap + without glasses)</span>
              </div>
              <div className="feature-pill">
                <span className="pill-check">✓</span>
                <span>6 Directional & Diagonal Head Poses</span>
              </div>
            </div>
          </div>

          {/* Hero Right Column: Embedded Registration Form OR Active Session Card */}
          <div className="hero-right-col">
            {userName ? (
              <div className="landing-active-session-card">
                <div className="active-session-badge">Active Participant</div>
                <h3>Welcome back, {userName}!</h3>
                <p>Your session is initialized. Continue your photo verification capture or inspect status in your dashboard.</p>
                <div className="session-card-actions">
                  <button
                    type="button"
                    className="btn-hero-primary"
                    onClick={() => navigate(`/upload/${encodeURIComponent(userName)}`)}
                  >
                    Resume Verification Capture →
                  </button>
                  <button
                    type="button"
                    className="btn-hero-secondary"
                    onClick={() => navigate(`/dashboard/${encodeURIComponent(userName)}`)}
                  >
                    View Submissions Dashboard
                  </button>
                  <button
                    type="button"
                    className="btn-session-logout"
                    onClick={handleLogout}
                  >
                    Sign Out / Switch Account
                  </button>
                </div>
              </div>
            ) : (
              <div className="landing-register-card">
                <div className="register-card-header">
                  <h2 className="register-card-title">User Registration</h2>
                  <p className="register-card-sub">
                    Fill in your details below to start verification immediately
                  </p>
                </div>

                <form onSubmit={handleRegisterSubmit} className="embedded-reg-form">
                  {/* 1. Full Name */}
                  <div className="reg-field-group">
                    <label htmlFor="reg-name">Full Name:</label>
                    <input
                      type="text"
                      id="reg-name"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      placeholder="Enter your full name"
                      autoComplete="name"
                      required
                    />
                  </div>

                  {/* 2. Email */}
                  <div className="reg-field-group">
                    <label htmlFor="reg-email">Email:</label>
                    <input
                      type="email"
                      id="reg-email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      placeholder="Enter your email address"
                      autoComplete="email"
                      required
                    />
                  </div>

                  {/* 3. Mobile Number */}
                  <div className="reg-field-group">
                    <label htmlFor="reg-mobile">Mobile Number:</label>
                    <input
                      type="tel"
                      id="reg-mobile"
                      name="mobile_no"
                      value={formData.mobile_no}
                      onChange={handleFormChange}
                      placeholder="Enter your mobile number"
                      autoComplete="tel"
                      required
                    />
                  </div>

                  {/* 4. Date of Birth */}
                  <div className="reg-field-group">
                    <label htmlFor="reg-dob">Date of Birth:</label>
                    <input
                      type="date"
                      id="reg-dob"
                      name="dob"
                      value={formData.dob}
                      onChange={handleFormChange}
                      required
                    />
                  </div>

                  {/* 5. Operating System (Android / iOS) */}
                  <div className="reg-field-group">
                    <label htmlFor="reg-os">Operating System:</label>
                    <select
                      id="reg-os"
                      name="os"
                      value={formData.os}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">-- Select Operating System --</option>
                      <option value="Android">Android</option>
                      <option value="iOS">iOS</option>
                    </select>
                  </div>

                  {/* 6. OS Version (Dependent) */}
                  {formData.os && (
                    <div className="reg-field-group">
                      <label htmlFor="reg-os-version">
                        {formData.os} Version:
                      </label>
                      <select
                        id="reg-os-version"
                        name="os_version"
                        value={formData.os_version}
                        onChange={handleFormChange}
                        required
                      >
                        <option value="">-- Select {formData.os} Version --</option>
                        {currentVersionList.map((ver) => (
                          <option key={ver.val} value={ver.val}>
                            {ver.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* 6b. Custom Version Text Field */}
                  {formData.os_version === 'Other' && (
                    <div className="reg-field-group">
                      <label htmlFor="reg-custom-version">Specific {formData.os} Version:</label>
                      <input
                        type="text"
                        id="reg-custom-version"
                        name="custom_version"
                        value={formData.custom_version}
                        onChange={handleFormChange}
                        placeholder="e.g. 14.1.2"
                        required
                      />
                    </div>
                  )}

                  {/* 7. Consent Agreement */}
                  <div className="reg-consent-group">
                    <label className="reg-checkbox-label">
                      <input
                        type="checkbox"
                        checked={consentAccepted}
                        onChange={(e) => setConsentAccepted(e.target.checked)}
                        required
                      />
                      <span>
                        I agree to the <Link to="/consent" target="_blank" rel="noopener noreferrer">Consent Agreement</Link> and permit processing of my photos.
                      </span>
                    </label>
                  </div>

                  {regError && <div className="reg-error-banner">⚠️ {regError}</div>}

                  <button
                    type="submit"
                    className="btn-hero-primary reg-submit-btn"
                    disabled={loading}
                  >
                    {loading ? 'Registering...' : 'Register & Start Verification →'}
                  </button>

                  <div className="reg-alt-link-row">
                    <span>Already registered? </span>
                    <button
                      type="button"
                      className="link-btn-signin"
                      onClick={() => navigate('/login')}
                    >
                      Sign In with Password
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* 3. The 3-Step Process (How It Works) */}
      <section className="landing-process-section">
        <div className="landing-process-inner">
          <h2 className="landing-section-heading">How it works</h2>
          <p className="landing-section-subhead">
            The verification takes approximately 10 minutes. Please ensure you are in a well-lit room.
          </p>

          <div className="process-cards-grid">
            {/* Step 1 */}
            <div className="process-card">
              <div className="process-step-num-circle">1</div>
              <h3 className="process-card-title">Historical Photos</h3>
              <p className="process-card-text">
                Start by uploading at least 3 genuine past photos from your gallery across different life stages with zero timing clauses or restrictions.
              </p>
            </div>

            {/* Step 2 */}
            <div className="process-card">
              <div className="process-step-num-circle">2</div>
              <h3 className="process-card-title">Live Selfies</h3>
              <p className="process-card-text">
                Capture 5 present-day selfies using your camera with green oval assurance ring. At least 1 photo must be taken without glasses.
              </p>
            </div>

            {/* Step 3 */}
            <div className="process-card">
              <div className="process-step-num-circle">3</div>
              <h3 className="process-card-title">Head Poses</h3>
              <p className="process-card-text">
                Capture 8 directional and diagonal head angles (Up, Down, Left, Right and 4 diagonal bands) guided by 3D alignment indicators.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Trust & Compliance Banner */}
      <footer className="landing-compliance-footer">
        <div className="compliance-inner">
          <div className="compliance-badge-pill">
            <span>🔒</span>
            <span>Enterprise KYC & Privacy Compliant</span>
          </div>
          <p className="compliance-disclaimer-text">
            Your biometric data is encrypted in transit and at rest with end-to-end TLS encryption.
            It is processed strictly for authentic face verification and synthetic modeling according to privacy standards.
          </p>
        </div>
      </footer>

      {/* 5. Requirements Modal */}
      {showReqModal && (
        <div className="landing-modal-overlay" onClick={() => setShowReqModal(false)}>
          <div className="landing-modal-content" onClick={e => e.stopPropagation()}>
            <button
              type="button"
              className="landing-modal-close-btn"
              onClick={() => setShowReqModal(false)}
            >
              &times;
            </button>
            <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.3rem', fontWeight: 800, color: '#0f172a' }}>
              📋 Verification Requirements
            </h3>
            <p style={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.5, margin: '0 0 1.25rem 0' }}>
              To ensure immediate acceptance by our automated verification system, please follow these guidelines:
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem', marginBottom: '1.5rem' }}>
              <div style={{ background: '#f8fafc', padding: '0.85rem 1rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <strong style={{ fontSize: '0.92rem', color: '#0f172a' }}>1. Historical Photos (Step 1 - Min 3 from Gallery)</strong>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.82rem', color: '#475569', lineHeight: 1.4 }}>
                  First step: upload genuine solo portraits from different life stages. Zero cooldown clauses or artificial delays.
                </p>
              </div>

              <div style={{ background: '#f8fafc', padding: '0.85rem 1rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <strong style={{ fontSize: '0.92rem', color: '#0f172a' }}>2. Present Selfies (Step 2 - 5 Total)</strong>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.82rem', color: '#475569', lineHeight: 1.4 }}>
                  Taken in front of different backgrounds. At least 1 selfie must be without glasses. 60-second cooldown between live captures.
                </p>
              </div>

              <div style={{ background: '#f8fafc', padding: '0.85rem 1rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <strong style={{ fontSize: '0.92rem', color: '#0f172a' }}>3. Head Poses (Step 3 - 8 Total)</strong>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.82rem', color: '#475569', lineHeight: 1.4 }}>
                  4 Directional (Up, Down, Left, Right) + 4 Diagonals (Up-Left, Up-Right, Down-Left, Down-Right). Auto-capture locks when oval turns green.
                </p>
              </div>
            </div>

            <button
              type="button"
              className="btn-hero-primary"
              style={{ width: '100%', padding: '0.85rem' }}
              onClick={() => setShowReqModal(false)}
            >
              Close Guidelines
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
